/**
 * Data Access Layer — Repository Pattern
 *
 * Single abstraction for ALL data persistence in Meester djay.
 * Currently backed by localStorage; designed for painless backend migration.
 *
 * USAGE:
 *   import { dataLayer } from '../data/dataLayer'
 *   const group = await dataLayer.getGroup()
 *   await dataLayer.setGroup('6')
 *
 * ARCHITECTURE:
 * - Each domain lives in its own file under src/data/domains/
 * - Shared helpers (readJson, writeJson, etc.) in src/data/helpers.ts
 * - This file composes all domains into the single `dataLayer` export
 * - ALL functions are async (even in localStorage mode)
 * - USE_BACKEND flag switches between localStorage and API calls
 *
 * MIGRATION STATUS: See docs/DATALAYER_MIGRATION.md
 *
 * @created 1 april 2026
 * @refactored 30 april 2026 — Split from 1008-line god-file into domain modules
 */

import { USE_BACKEND } from './helpers';

// ─── Domain Imports ───────────────────────────────────────────────────────────

import { getGroup, setGroup, getPeriod, setPeriod, clearPeriod } from './domains/preferences';
import { loadMultiAccountState, saveMultiAccountState } from './domains/accountState';
import { getAssessmentBlueprint, getAllAssessmentBlueprints, saveAssessmentBlueprint, getAssessmentAttempt, getAllAssessmentAttempts, saveAssessmentAttempt, getAssessmentReport, getAllAssessmentReports, saveAssessmentReport, deleteAssessmentAttempt, clearAllAssessmentData, getAssessmentSession, saveAssessmentSession, clearAssessmentSession } from './domains/assessment';
import { getLearnerPreferences, saveLearnerPreferences } from './domains/learnerPrefs';
import type { LearnerPreferences } from './domains/learnerPrefs';
import { getGovernanceConfig, saveGovernanceConfig, resetGovernanceConfig } from './domains/governance';
import { getDjayVariant, saveDjayVariant, getDjayLongPressDiscovered, saveDjayLongPressDiscovered } from './domains/djay';
import { getAdminSettings, saveAdminSettings } from './domains/admin';
import { getAdaptiveConfig, saveAdaptiveConfig, resetAdaptiveConfig } from './domains/adaptiveConfig';
import { getFluencyIntroSeen, setFluencyIntroSeen, getSprintIntroSeen, setSprintIntroSeen, getFreePracticeIntroSeen, setFreePracticeIntroSeen, getChallengeIntroSeen, setChallengeIntroSeen, getKlascodeBannerDismissed, setKlascodeBannerDismissed, getLastOefenlabSession, saveLastOefenlabSession, getNewlyUnlockedChallenge, setNewlyUnlockedChallenge, clearNewlyUnlockedChallenge, getPersonalRecords, savePersonalRecords } from './domains/oefenlab';
import type { PersonalRecords } from './domains/oefenlab';
import { getTeacherNote, saveTeacherNote } from './domains/teacherNotes';
import { getTeacherAssignments, saveTeacherAssignment, deleteTeacherAssignment, archiveTeacherAssignment, markAssignmentCompletedByStudent } from './domains/teacherAssignments';
import { getPracticeAssignments, savePracticeAssignment, deletePracticeAssignment, archivePracticeAssignment, markPracticeAssignmentCompletedByStudent } from './domains/practiceAssignments';
import { getStorageStats } from './domains/storageStats';

// ═══════════════════════════════════════════════════════════════════════════════
// Public API — Single export object
// ═══════════════════════════════════════════════════════════════════════════════

export const dataLayer = {
  // Config
  isBackendMode: USE_BACKEND,
  // D1: App Preferences
  getGroup,
  setGroup,
  getPeriod,
  setPeriod,
  clearPeriod,
  // D2: Multi-Account State
  loadMultiAccountState,
  saveMultiAccountState,
  // D3: Assessment Storage
  getAssessmentBlueprint,
  getAllAssessmentBlueprints,
  saveAssessmentBlueprint,
  getAssessmentAttempt,
  getAllAssessmentAttempts,
  saveAssessmentAttempt,
  getAssessmentReport,
  getAllAssessmentReports,
  saveAssessmentReport,
  deleteAssessmentAttempt,
  clearAllAssessmentData,
  // D4: Assessment Session Recovery
  getAssessmentSession,
  saveAssessmentSession,
  clearAssessmentSession,
  // D5: Learner Preferences
  getLearnerPreferences,
  saveLearnerPreferences,
  // D6: Governance Config
  getGovernanceConfig,
  saveGovernanceConfig,
  resetGovernanceConfig,
  // D7: Djay Selection
  getDjayVariant,
  saveDjayVariant,
  getDjayLongPressDiscovered,
  saveDjayLongPressDiscovered,
  // D8: Admin Settings
  getAdminSettings,
  saveAdminSettings,
  // D8b: Adaptive Engine Config (V11_CONFIG runtime overrides — P-14)
  getAdaptiveConfig,
  saveAdaptiveConfig,
  resetAdaptiveConfig,
  // D9: Oefenlab UI State
  getFluencyIntroSeen,
  setFluencyIntroSeen,
  getSprintIntroSeen,
  setSprintIntroSeen,
  getFreePracticeIntroSeen,
  setFreePracticeIntroSeen,
  getChallengeIntroSeen,
  setChallengeIntroSeen,
  getKlascodeBannerDismissed,
  setKlascodeBannerDismissed,
  getLastOefenlabSession,
  saveLastOefenlabSession,
  getNewlyUnlockedChallenge,
  setNewlyUnlockedChallenge,
  clearNewlyUnlockedChallenge,
  getPersonalRecords,
  savePersonalRecords,
  // D10: Teacher Notes
  getTeacherNote,
  saveTeacherNote,
  // D11: Teacher Assessment Assignments
  getTeacherAssignments,
  saveTeacherAssignment,
  deleteTeacherAssignment,
  archiveTeacherAssignment,
  markAssignmentCompletedByStudent,
  // D13: Teacher Practice Assignments
  getPracticeAssignments,
  savePracticeAssignment,
  deletePracticeAssignment,
  archivePracticeAssignment,
  markPracticeAssignmentCompletedByStudent,
  // D12: Storage Statistics (D-SCAN D-8)
  getStorageStats
} as const;

// ─── Re-exported Types ────────────────────────────────────────────────────────

export type { LearnerPreferences, PersonalRecords };
export type DataLayer = typeof dataLayer;